import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import * as ROUTES from '../../constants/routes';
import { nanoid } from 'nanoid';

import FormCard from './formCard/FormCard';

import { pages } from './pageNumberForm';
import { getAuthUser, getUserUid } from '../../static/localStorage';
import { convertDateToFirestoreTimestamp } from '../../firebase/api/firestoreUtils';
import { createPost } from '../../firebase/api/post';
import { sendGridEmail } from '../../firebase/api/mail';
import { getInviteToAnswer } from '../../templates';
import PropTypes from 'prop-types';

import './ProjectCreationForm.css';
import { createUser, getUserByEmail } from '../../firebase/api/users';

const initialState = {
    title: '',
    description: '',
    brief: '',
    category: '',
    budget: '',
    commitmentLevel: '',
    deadline: '',
    isPublicPost: false,
    invitedTeams: [],
    invitedIndividuals: [],
};

const pageOneFields = {
    title: Yup.string().max(55).required('Required'),
    description: Yup.string().min(6, 'to short').max(500, 'to long').required('Required'),
    brief: Yup.string().min(6, 'to short').max(250, 'to long').url(),
    category: Yup.string().required('Required'),
    budget: Yup.string().required('Required'),
    commitmentLevel: Yup.string().required('Required'),
    deadline: Yup.string().required('Required'),
};

const pageTwoFields = {
    invitedTeams: Yup.mixed().when({
        is: Array.isArray,
        then: Yup.array().required('Required'),
        otherwise: Yup.string().required('required'),
    }),
    invitedIndividuals: Yup.mixed().when({
        is: Array.isArray,
        then: Yup.array().required('Required'),
        otherwise: Yup.string().required('required'),
    }),
};

const pageOneValidationSchema = Yup.object({ ...pageOneFields });
const allFieldsValidationSchema = Yup.object({ ...pageOneFields, ...pageTwoFields });
const status = 'new';

const ProjectCreationForm = ({ page, setPage }) => {
    const navigate = useNavigate();
    const modifyCreatePostData = async dto => {
        if (!dto.isPublicPost) {
            for (let i = 0; i < dto.invitedTeams.length; i++) {
                // logic of filter users from teams who already exist in invitedIndividuals list
                const invitedEmails = dto.invitedIndividuals.map(item => item.email);
                dto.invitedTeams[i].invitedUsers = dto.invitedTeams[i].invitedUsers.filter(
                    item => !invitedEmails.includes(item)
                ); // for fetching data of those users who are not in the invitedIndividuals list
                let invitedUsersFromTeams = [];
                for (let j = 0; j < dto.invitedTeams[i].invitedUsers.length; j++) {
                    const user = await getUserByEmail(dto.invitedTeams[i].invitedUsers[j]);

                    if (user !== null) {
                        invitedUsersFromTeams.push({
                            firstName: user.firstName,
                            lastName: user.lastName,
                            name: user.firstName
                                ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`
                                : `${user.email}`,
                            email: user.email,
                            uid: user.uid,
                            position: user.position || '',
                            website: user.website || '',
                        });
                    }
                }
                dto.invitedIndividuals = [...dto.invitedIndividuals, ...invitedUsersFromTeams];
            }
            for (let i = 0; i < dto.invitedIndividuals.length; i++) {
                if (dto.invitedIndividuals[i].uid === dto.invitedIndividuals[i].email) {
                    const user = await getUserByEmail(dto.invitedIndividuals[i].email);
                    if (user === null) {
                        // if project owner add freelancer by email and this email is not registered in sidekick
                        let uid = nanoid();
                        dto.invitedIndividuals[i].uid = uid;
                        dto.invitedIndividuals[i] = await createUser(
                            uid,
                            dto.invitedIndividuals[i]
                        );
                    } else {
                        // if added email already registered on sidekick
                        dto.invitedIndividuals[i] = user;
                    }
                }
            }
        }

        dto.publisherUid = getUserUid();
        dto.status = status;
        dto.assignTo = null;
        dto.responseEmails = [];
        dto.reviewIds = [];
        dto.createdTimestamp = convertDateToFirestoreTimestamp(new Date());
        dto.isDeleted = false;
        dto.deleteReason = '';
        dto.isApprovedByAdmin = !dto.isPublicPost;
        dto.isReviewedByAdmin = !dto.isPublicPost;
        dto.isDeletedByAdmin = false;
        dto.totalDonations = 0;
        dto.inviteColleagues = !dto.isPublicPost
            ? [...dto.invitedIndividuals]
            : dto.invitedIndividuals;
        dto.unreviewed = !dto.isPublicPost ? [...dto.inviteColleagues] : [];
        dto.unviewed = !dto.isPublicPost ? [...dto.inviteColleagues] : [];
        dto.viewed = [];
        delete dto.invitedIndividuals;
        delete dto.invitedTeams;
    };

    const sendMails = useCallback(
        async (projectTitle, projectDescription, recipients, id, category, notionLink, budget) => {
            const currentUser = getAuthUser();

            await sendGridEmail(
                getInviteToAnswer(
                    currentUser.email,
                    projectTitle,
                    projectDescription,
                    recipients,
                    currentUser.firstName,
                    currentUser.lastName,
                    currentUser.uid,
                    `${window.location.origin}/posts/${id}`,
                    id,
                    category,
                    notionLink,
                    budget
                )
            );
        },
        []
    );

    const handleSubmit = useCallback(
        async (values, { setSubmitting, errors, resetForm }) => {
            if (page === pages.firstPage && !errors) {
                setPage(pages.secondPage);
                setSubmitting(false);
            }
            if (page === pages.secondPage) {
                await modifyCreatePostData(values);
                await createPost(values)
                    .then(async id => {
                        if (!values.isPublicPost) {
                            await sendMails(
                                values.title,
                                values.description,
                                values.inviteColleagues,
                                id,
                                values.category,
                                values.notion || values.otherLink,
                                values.budget
                            );
                        }

                        resetForm();
                        setSubmitting(false);
                        navigate(ROUTES.MY_ALL_POSTS);
                    })
                    .catch(error => alert(error.message));
            }
        },
        [page, navigate, setPage, sendMails]
    );

    return (
        <Formik
            initialValues={initialState}
            validationSchema={
                page === pages.firstPage ? pageOneValidationSchema : allFieldsValidationSchema
            }
            onSubmit={handleSubmit}
        >
            {({ values, setValues, isSubmitting, errors, touched, handleChange }) => {
                return (
                    <FormCard
                        page={page}
                        setPage={setPage}
                        values={values}
                        setValues={setValues}
                        isSubmitting={isSubmitting}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                    />
                );
            }}
        </Formik>
    );
};

ProjectCreationForm.propTypes = {
    page: PropTypes.number.isRequired,
    setPage: PropTypes.func.isRequired,
};

export default ProjectCreationForm;
